/**
 * SimpleHubLinks — where everything that left Simple's sidebar went (ob-2).
 *
 * Simple mode dropped from 34 sidebar leaves to six rows. The rule for the cut
 * was never "make the list short" — it was **lose nothing**: each screen that
 * survived absorbed the surfaces that belong to it, and this strip is how you
 * see them. Payments Received under Sales, What to Pay under Purchases,
 * Month-end under Reports.
 *
 * Rendered once by `AppLayout`, directly under the page title, and only in
 * Simple mode. Three filters decide what actually appears:
 *
 *   1. the map for this route (`SIMPLE_HUB_LINKS`),
 *   2. minus anything the modules layer says this org doesn't use,
 *   3. minus anything already showing as a sidebar row — nothing is offered
 *      twice, so the strip shrinks as the sidebar grows.
 *
 * @since ob-2 (2026-07-28)
 */
import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
    ArrowLeftRight, BadgeCheck, BarChart3, Bike, Bot, Building2, CalendarDays, Calculator,
    ChefHat, CreditCard, Factory, FileCheck, Gift, Landmark, Package, PackageCheck, Receipt,
    RotateCcw, ScanLine, Send, ShieldCheck, ShoppingCart, UserCircle, Utensils, Wallet,
    Warehouse, ArrowRight, FileSpreadsheet,
} from 'lucide-react';
import { simpleHubLinksFor, SIMPLE_HUB_LINKS } from '../config/simpleNav';
import { isNavItemVisibleByModule } from '../config/modules';
import { useModules } from '../hooks/useModules';
import type { NavItem } from './sidebarNav';

const ICONS: Record<string, typeof Wallet> = {
    ArrowLeftRight, BadgeCheck, BarChart3, Bike, Bot, Building2, CalendarDays, Calculator,
    ChefHat, CreditCard, Factory, FileCheck, Gift, Landmark, Package, PackageCheck, Receipt,
    RotateCcw, ScanLine, Send, ShieldCheck, ShoppingCart, UserCircle, Utensils, Wallet, Warehouse, FileSpreadsheet,
};

/**
 * Every hub-strip destination as a `NavItem`, deduped by href.
 *
 * The sidebar's ★ Favorites strip resolves pinned hrefs against the items
 * currently in the nav and drops the ones it cannot find. Without this, a
 * Simple user who had pinned "Payments Received" would have watched their pin
 * disappear the day Simple went to six rows — the screen still reachable, but
 * their own shortcut to it gone. AppLayout feeds these to the sidebar so pins
 * survive the cut.
 */
export const SIMPLE_HUB_NAV_ITEMS: NavItem[] = (() => {
    const byHref = new Map<string, NavItem>();
    for (const links of Object.values(SIMPLE_HUB_LINKS)) {
        for (const l of links) {
            if (byHref.has(l.href)) continue;
            const Icon = ICONS[l.iconName] ?? ArrowRight;
            byHref.set(l.href, { id: l.id, label: l.label, icon: <Icon size={16} />, href: l.href });
        }
    }
    return [...byHref.values()];
})();

export interface SimpleHubLinksProps {
    /** The route being shown. */
    route: string;
    /** Leaf ids currently in the sidebar — those are dropped from the strip. */
    navItemIds: ReadonlySet<string>;
}

export function SimpleHubLinks({ route, navItemIds }: SimpleHubLinksProps) {
    const navigate = useNavigate();
    const { active } = useModules();

    const links = useMemo(
        () => simpleHubLinksFor(route).filter(
            l => !navItemIds.has(l.id) && isNavItemVisibleByModule(l.id, active),
        ),
        [route, navItemIds, active],
    );

    if (links.length === 0) return null;

    return (
        <nav
            aria-label="Also on this screen"
            className="-mt-1 mb-5 flex flex-wrap items-center gap-2"
        >
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400 dark:text-stone-500">
                Also here
            </span>
            {links.map(link => {
                const Icon = ICONS[link.iconName] ?? ArrowRight;
                return (
                    <button
                        key={link.id}
                        type="button"
                        onClick={() => navigate({ to: link.href })}
                        className="group inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1 text-[12px] font-medium text-stone-600 transition-colors hover:border-teal-300 hover:bg-teal-50 hover:text-teal-800 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300 dark:hover:border-teal-800 dark:hover:bg-teal-950/30 dark:hover:text-teal-300"
                    >
                        <Icon size={13} className="text-stone-400 group-hover:text-teal-600 dark:text-stone-500" />
                        {link.label}
                    </button>
                );
            })}
        </nav>
    );
}
